import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { annotationModeAtom } from '@/store/annotationModeAtom';
import { isAdminPopup, getAdminOrigin, postToAdmin, MSG, type AdminMessage } from '@/lib/adminAnnotation';

/** Public routes where annotation mode must never activate (share links opened from email). */
const ANNOTATION_EXCLUDED_PATHS = ['/share/', '/examshareaccess/', '/home/examshareaccess/'];

function isExcludedPath(): boolean {
  const path = window.location.pathname.toLowerCase();
  return ANNOTATION_EXCLUDED_PATHS.some(p => path.startsWith(p));
}

/**
 * Mounts once at app root. Handles annotation mode activation and postMessage bridging.
 *
 * Security:
 *  - Popup mode (LivePreviewButton): activates immediately if isAdminPopup() is true.
 *    isAdminPopup() requires PatientAdminPortalURL to be configured, so annotation mode
 *    never fires on patient-only deployments. Public share routes are also excluded to
 *    prevent false activation when email clients set window.opener on share links.
 *  - Iframe mode (LivePreviewPane): activates only when the admin portal sends a trusted
 *    ADMIN_HANDSHAKE postMessage from the configured PatientAdminPortalURL origin.
 *  - All incoming messages are validated against PatientAdminPortalURL from l_system_config,
 *    read from localStorage at message-receive time (loaded by SystemConfigBootstrap).
 */
export function AdminAnnotationBridge() {
  const setAnnotationMode = useSetAtom(annotationModeAtom);
  const queryClient       = useQueryClient();

  // Popup mode: activate annotation mode immediately on mount.
  // isAdminPopup() requires PatientAdminPortalURL to be configured (getAdminOrigin() guard)
  // and the path must not be a public share route.
  useEffect(() => {
    if (!isExcludedPath() && isAdminPopup()) setAnnotationMode(true);
  }, [setAnnotationMode]);

  // Message handler for iframe mode (LivePreviewPane sends ADMIN_HANDSHAKE on load).
  // adminOrigin is read from localStorage at message-receive time so there is no
  // race condition with SystemConfigBootstrap — by the time any message arrives
  // (requires a user action in the admin UI), system config will have loaded.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const adminOrigin = getAdminOrigin();
      if (!adminOrigin || event.origin !== adminOrigin) return;

      const msg = event.data as AdminMessage;
      if (!msg?.type) return;

      // Iframe mode: admin sends HANDSHAKE first, we respond and activate.
      if (msg.type === MSG.HANDSHAKE) {
        setAnnotationMode(true);
        postToAdmin({ type: MSG.HANDSHAKE_ACK });
      }

      if (msg.type === MSG.REFRESH_CONTENT) {
        queryClient.invalidateQueries({ queryKey: ['content'] });
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setAnnotationMode, queryClient]);

  return null;
}
